import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import {
  Grid,
  Typography,
  Button,
  FormControlLabel,
  RadioGroup,
  FormControl,
  Radio,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { getMemberDetail, getClientMemberDetail } from '@apps/shared/src/getMember/actions';
import {
  createCaseInfo,
  resetAddCase,
  setAddCaseResponseInfo,
  updateCaseMemberDetail,
  setCaseMemberInfo,
  clearPlanHealthInfo,
  setSubmitterContactInfo,
  setIsDependentMember,
} from '@apps/shared/src/addCase/actions';
import { MemberInfo, SubmitterContactInfo } from '@apps/shared/src/getMember/types/memberTypes';
import { AddCaseState, ProviderInfo } from '@apps/shared/src/addCase/types/addCaseTypes';
import { getDependentMembersDetail } from '@apps/shared/src/dependentMember/actions';
import { DependentMemberInfo } from '@apps/shared/src/dependentMember/types/dependentMemberTypes';
import LoadingSpinner from '@apps/shared/src/components/LoadingSpinner';
import { defaultMemberState } from '@apps/shared/src/getMember/reducer';
import { RootState } from '../store';
import TextInput from '../shared/textInput';
import SelectInput from '../shared/muiSelect';
import formatDate from '../shared/date';

import AppLayout from '../shared/AppLayout';

const useStyles = makeStyles(() =>
  createStyles({
    appContainer: {
      padding: '1rem',
      color: '#797C80',
      position: 'relative',
      height: 'calc(100vh - 40px - 2.5rem)',
      scroll: 'auto',
    },
    arrowIcon: {
      height: '2rem',
      width: '2rem',
      cursor: 'pointer',
    },
    formContainer: {
      color: '#797C80',
      height: 'calc(100vh - 20rem)',
      padding: '1.5rem 0',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'scroll',
      '&::-webkit-scrollbar': {
        width: '1px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '1px',
      },
    },
    inputSpace: {
      display: 'grid',
      gap: '1.5rem',
      gridTemplateColumns: 'repeat(auto-fit, minmax(30%, 1fr))',
    },
    heading: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      color: '#504f4f',
      marginBottom: '1rem',
    },
    loadingSpinner: {
      margin: '3rem auto 0 auto',
    },
    dependentWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    radioGroup: {
      '&.MuiFormGroup-root': {
        flexWrap: 'nowrap !important',
        justifyContent: 'space-between',
        padding: '0 8rem 0 0',
      },
    },
    caution: {
      gridColumn: '1 / -1',
    },
    searchBtn: {
      marginTop: '1.7rem',
      padding: '11px 20px',
      backgroundColor: '#1976d2',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      height: 'fit-content',
    },
    buttonStyle: {
      width: '32%',
      position: 'absolute',
      bottom: 0,
      right: '1.5rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1.5rem',
    },
    backBtn: {
      gridColumn: '1 / -1',
      width: '50%',
      borderRadius: '.2rem',
      background: '#fff',
      color: '#888787',
      border: '1px solid #888787',
      padding: '.5rem 3rem',
      cursor: 'pointer',
      boxShadow: 'None',
    },
    successBtn: {
      gridColumn: '1 / -1',
      width: '50%',
      borderRadius: '.2rem',
      background: 'primary',
      color: 'white',
      fontWeight: 600,
      outline: 'None',
      padding: '.5rem 3rem',
      cursor: 'pointer',
      boxShadow: 'None',
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(255, 255, 255, 0)',
      zIndex: -1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      transition: 'background 0.3s ease, opacity 0.3s ease',
    },
    overlayVisible: {
      background: 'rgba(255, 255, 255, 0.8)',
      opacity: 1,
      zIndex: 999,
    },
    errorLabel: {
      color: 'red',
    },
  })
);

type StateProps = {
  dependentMembers: DependentMemberInfo[];
  memberInfo: MemberInfo;
  memberDetailsLoaded: boolean;
  caseType: string;
  selectedPlan: string;
  providerOption: boolean;
  addingCase: boolean;
  isDependentMember: boolean;
  isCaseCreated: boolean;
  hasMemberSubmitRole: boolean;
  hasClientSubmitRole: boolean;
  newCaseInfo: MemberInfo;
};

type DispatchProps = {
  updateCaseMemberDetail: (memberInfo: MemberInfo) => void;
  getMemberDetail: () => void;
  getClientMemberDetail: (
    patientID: string,
    patientName: string,
    patientDoB: string,
    masterplanID: string
  ) => void;
  createCaseInfo: (
    redirectTo: string,
    caseInfo: MemberInfo & ProviderInfo,
    caseType: string
  ) => void;
  getDependentMembersDetail: () => void;
  setAddCaseResponseInfo: (
    key: keyof AddCaseState,
    value: AddCaseState[keyof AddCaseState]
  ) => void;
  resetAddCase: () => void;
  setCaseMemberInfo: (key: keyof MemberInfo, value: MemberInfo[keyof MemberInfo]) => void;
  clearPlanHealthInfo: () => void;
  setSubmitterContactInfo: (
    key: keyof SubmitterContactInfo,
    value: SubmitterContactInfo[keyof SubmitterContactInfo]
  ) => void;
  setIsDependentMember: (isDependentMember: boolean) => void;
};

const mapDispatchToProps = {
  updateCaseMemberDetail,
  getMemberDetail,
  getClientMemberDetail,
  setAddCaseResponseInfo,
  resetAddCase,
  createCaseInfo,
  getDependentMembersDetail,
  setCaseMemberInfo,
  clearPlanHealthInfo,
  setSubmitterContactInfo,
  setIsDependentMember,
};

const mapStateToProps = (state: RootState) => ({
  dependentMembers: state.getDependentMember,
  memberInfo: state.getMember.memberInfo,
  memberDetailsLoaded: state.getMember.memberDetailsLoaded,
  caseType: state.addCase.caseType,
  providerOption: state.addCase.providerOption,
  isCaseCreated: state.addCase.isCreated,
  addingCase: state.addCase.addingCase,
  isDependentMember: state.addCase.isDependentMember,
  hasMemberSubmitRole:
    state.user.currentUser?.roles?.includes('medivi-casesubmissionmember') ?? false,
  hasClientSubmitRole:
    state.user.currentUser?.roles?.includes('medivi-casesubmissionclient') ?? false,
  newCaseInfo: state.addCase.memberInfo,
  selectedPlan: state.caseSelect.selectedPlan,
});

type Props = StateProps & DispatchProps;

const MemberDetail = ({
  memberInfo,
  memberDetailsLoaded,
  dependentMembers,
  caseType,
  updateCaseMemberDetail,
  getMemberDetail,
  getDependentMembersDetail,
  getClientMemberDetail,
  addingCase,
  isDependentMember,
  setCaseMemberInfo,
  clearPlanHealthInfo,
  setSubmitterContactInfo,
  newCaseInfo,
  hasMemberSubmitRole,
  hasClientSubmitRole,
  selectedPlan,
  setIsDependentMember,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();

  const [isAppointmentForDependentMember, setIsAppointmentForDependentMember] =
    useState<boolean>(false);
  const [dependentMemberId, setDependentMemberId] = useState<string>('');
  const isNewPatient = newCaseInfo?.newPatient ? 'new' : 'existing';

  const [patientType, setPatientType] = useState(isNewPatient);
  const [clientPatientType, setClientPatientType] = useState('');
  const [patientID, setPatientID] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientDOB, setPatientDOB] = useState('');
  const [errors, setErrors] = useState({ phone: '', email: '' });

  const handleNext = () => {
    let recordType, route;

    if (caseType === 'Balance Bill') {
      recordType = 'BalanceBill';
      route = '/upload-file';
    } else if (caseType === 'Call Ahead') {
      recordType = 'CallAhead';
      route = '/provider-detail';
    } else if (caseType === 'Appeal') {
      recordType = 'Appeal';
      route = '/provider-detail';
    } else {
      recordType = 'AccessIssue';
      route = '/provider-detail';
    }

    const memberData: any = {
      ...newCaseInfo,
      recordType,
    };

    updateCaseMemberDetail(memberData);
    history.push(route);
  };

  const handleBack = () => {
    history.goBack();
  };

  const validatePhone = (phone: string) => {
    const phoneError = /^\d{10}$/.test(phone) ? '' : 'Must be a valid phone number';
    setErrors(prevErrors => ({ ...prevErrors, phone: phoneError }));
  };

  const validateEmail = (email: string) => {
    const emailError = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? '' : 'Must be a valid email';
    setErrors(prevErrors => ({ ...prevErrors, email: emailError }));
  };

  const handleAppointmentFor = (value = 'Myself'): void => {
    if (value === 'Dependent') {
      getDependentMembersDetail();
      updateCaseMemberDetail(defaultMemberState.memberInfo);
      setIsAppointmentForDependentMember(true);
    } else {
      setDependentMemberId('');
      updateCaseMemberDetail(memberInfo);
      setIsAppointmentForDependentMember(false);
      setIsDependentMember(false);
    }
    getMemberDetail();
  };

  const handlePatientType = (field: keyof MemberInfo, value: string): void => {
    setCaseMemberInfo(field, value === 'new');
  };

  const handleOnChange = (value: string, field: keyof MemberInfo) => {
    setCaseMemberInfo(field, value);

    if (field === 'phone') validatePhone(value);
    if (field === 'email') validateEmail(value);

    if (hasClientSubmitRole && field !== 'email' && field !== 'phone') {
      clearPlanHealthInfo();
    }
  };

  const handlePatientIDChange = (value: string, field: keyof MemberInfo) => {
    setPatientID(value);
    setCaseMemberInfo(field, value);
  };

  const handlePatientNameChange = (value: string) => {
    setPatientName(value);

    const [firstName = '', ...rest] = value.trim().split(' ');
    const lastName = rest.join(' ');

    setCaseMemberInfo('firstName', firstName);
    setCaseMemberInfo('lastName', lastName);
  };

  const handleDateChange =
    (key: keyof MemberInfo) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setPatientDOB(e.currentTarget.value);
      setCaseMemberInfo(key, e.currentTarget.value);
    };

  const handleSubmitterContactinfo = (value: string, field: keyof SubmitterContactInfo) => {
    setSubmitterContactInfo(field, value);
  };

  const handleDependentMemberSelect = (selectedMemberId: string): void => {
    setDependentMemberId(selectedMemberId);
    const dependentMemberInfo = dependentMembers.find((member: DependentMemberInfo) => {
      return `${member.firstName} ${member.lastName}` === selectedMemberId;
    });
    if (dependentMemberInfo) {
      setIsDependentMember(true);
      updateCaseMemberDetail(dependentMemberInfo);
    }
  };

  const validEmail = (email: string): boolean => {
    if (email !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidCharactersRegex = /[&!#^$%*()''""~,`]/;
      return emailRegex.test(email) && !invalidCharactersRegex.test(email);
    }
    return true;
  };

  const validPhoneNumber = (phoneNumber: string): boolean => {
    if (phoneNumber !== '') {
      return phoneNumber.length === 10;
    }
    return true;
  };

  const validZipCode = (zipCode: string): boolean => {
    if (zipCode !== '') {
      return zipCode.length === 5;
    }
    return true;
  };

  const handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>, length: number): void => {
    let inputValue = e.target.value.replace(/[^0-9]/g, '');
    inputValue = inputValue.slice(0, length);
    e.target.value = inputValue;
  };

  const handleSearchClick = () => {
    getClientMemberDetail(patientID, patientName, patientDOB, selectedPlan);
  };

  const FormatDate = (input: any) => {
    const date = new Date(input);
    const formattedDate = date.toISOString().split('T')[0];
    return formattedDate;
  };

  useEffect(() => {
    if (caseType === '') {
      history.push(`/my-cases`);
    }
    if (hasMemberSubmitRole) {
      if (isDependentMember) {
        setDependentMemberId(`${newCaseInfo.firstName} ${newCaseInfo.lastName}`);
      }
      if (memberInfo && newCaseInfo.preferredMethodOfContact === '') {
        updateCaseMemberDetail(memberInfo);
        handleAppointmentFor();
      }
    }
  }, [memberDetailsLoaded]);

  useEffect(() => {
    if (hasClientSubmitRole && memberInfo) {
      updateCaseMemberDetail(memberInfo);
      if (memberInfo.phone) validatePhone(memberInfo.phone);
      if (memberInfo.email) validateEmail(memberInfo.email);
    }
  }, [memberInfo]);

  useEffect(() => {
    if (hasClientSubmitRole) {
      if (newCaseInfo.memberId) setPatientID(newCaseInfo.memberId);
      if (newCaseInfo.firstName) setPatientName(`${newCaseInfo.firstName} ${newCaseInfo.lastName}`);
      if (newCaseInfo.dateOfBirth) setPatientDOB(FormatDate(newCaseInfo.dateOfBirth));
      updateCaseMemberDetail(newCaseInfo);
      if (memberInfo.phone) validatePhone(newCaseInfo.phone);
      if (memberInfo.email) validateEmail(newCaseInfo.email);
    }
    // eslint-disable-next-line
  }, []);

  const disableNextAndSubmit: boolean = (() => {
    if (hasMemberSubmitRole) {
      return (
        !newCaseInfo ||
        !newCaseInfo.preferredMethodOfContact ||
        (newCaseInfo.preferredMethodOfContact === 'Email' &&
          (!newCaseInfo.email || !validEmail(newCaseInfo.email))) ||
        (newCaseInfo.preferredMethodOfContact === 'Phone' && !newCaseInfo.phone) ||
        !validPhoneNumber(newCaseInfo.phone) ||
        !validEmail(newCaseInfo.email) ||
        !validZipCode(newCaseInfo.zip)
      );
    }

    if (hasClientSubmitRole) {
      const isAccessIssueAndMissingPatientType = caseType === 'Access Issue' && !clientPatientType;

      return (
        !newCaseInfo ||
        !newCaseInfo?.submitterContactInfo?.firstName ||
        !newCaseInfo?.submitterContactInfo?.lastName ||
        !newCaseInfo?.submitterContactInfo?.companyName ||
        !newCaseInfo?.submitterContactInfo?.phoneNumber ||
        !newCaseInfo?.submitterContactInfo?.emailAddress ||
        !newCaseInfo.memberId ||
        !newCaseInfo.firstName ||
        !newCaseInfo.lastName ||
        !newCaseInfo.dateOfBirth ||
        !newCaseInfo.phone ||
        !newCaseInfo.email ||
        !newCaseInfo.address ||
        !newCaseInfo.city ||
        !newCaseInfo.state ||
        !newCaseInfo.zip ||
        isAccessIssueAndMissingPatientType ||
        !validPhoneNumber(newCaseInfo.phone) ||
        !validPhoneNumber(newCaseInfo?.submitterContactInfo?.phoneNumber) ||
        !validEmail(newCaseInfo.email) ||
        !validEmail(newCaseInfo?.submitterContactInfo?.emailAddress) ||
        !validZipCode(newCaseInfo.zip)
      );
    }

    return true;
  })();

  const disableSearch: boolean = (() => {
    return !patientID || !patientName || !patientDOB;
  })();

  return (
    <AppLayout>
      <div className={`${classes.overlay} ${addingCase ? classes.overlayVisible : ''}`}>
        <LoadingSpinner />
      </div>
      <div className={classes.appContainer}>
        <ArrowBackIcon className={classes.arrowIcon} onClick={handleBack} />
        <Typography>
          {`** Please do not submit ${caseType.toLowerCase()}s from multiple providers on the same submission.`}
        </Typography>

        <div className={classes.formContainer}>
          <div>
            {hasClientSubmitRole && (
              <>
                <Typography className={classes.heading}>
                  Submitter Contact Information<span style={{ color: 'red' }}>*</span>
                </Typography>
                <div className={classes.inputSpace}>
                  <TextInput
                    label="First Name*"
                    value={`${newCaseInfo?.submitterContactInfo?.firstName || ''}`}
                    onChange={val => handleSubmitterContactinfo(val, 'firstName')}
                  />
                  <TextInput
                    label="Last Name*"
                    value={`${newCaseInfo?.submitterContactInfo?.lastName || ''}`}
                    onChange={val => handleSubmitterContactinfo(val, 'lastName')}
                  />
                  <TextInput
                    label="Company Name*"
                    value={`${newCaseInfo?.submitterContactInfo?.companyName || ''}`}
                    onChange={val => handleSubmitterContactinfo(val, 'companyName')}
                  />
                  <TextInput
                    label="Phone*"
                    value={newCaseInfo?.submitterContactInfo?.phoneNumber}
                    onChange={val => handleSubmitterContactinfo(val, 'phoneNumber')}
                    validators={['validatePhoneNumberLength']}
                    errorMessages={['Must be a valid phone number']}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      onInput: e => handleNumericInput(e, 10),
                    }}
                  />
                  <TextInput
                    label="Email*"
                    value={newCaseInfo?.submitterContactInfo?.emailAddress}
                    onChange={val => handleSubmitterContactinfo(val, 'emailAddress')}
                    validators={['isEmail']}
                    errorMessages={['Must be a valid email']}
                  />
                </div>
              </>
            )}
          </div>
          {hasClientSubmitRole && (
            <Typography className={classes.heading}>
              Patient Information<span style={{ color: 'red' }}>*</span>
            </Typography>
          )}
          <div className={classes.inputSpace}>
            {hasMemberSubmitRole && (
              <div className={classes.dependentWrapper}>
                <FormControl>
                  {caseType === 'Access Issue' && (
                    <Typography>Who is this appointment for?</Typography>
                  )}
                  <RadioGroup
                    row
                    onChange={e => handleAppointmentFor(e.target.value)}
                    defaultValue={isDependentMember ? 'Dependent' : 'Myself'}
                    className={classes.radioGroup}
                  >
                    <FormControlLabel
                      control={<Radio color="primary" />}
                      value="Myself"
                      label="Myself"
                    />
                    <FormControlLabel
                      control={<Radio color="primary" />}
                      value="Dependent"
                      label="Dependent"
                    />
                  </RadioGroup>
                </FormControl>

                {(isAppointmentForDependentMember || isDependentMember) && (
                  <SelectInput
                    label="Select Dependent Member"
                    options={dependentMembers.map(
                      (dependentMember: DependentMemberInfo) =>
                        `${dependentMember.firstName} ${dependentMember.lastName}`
                    )}
                    value={dependentMemberId}
                    onSelectChange={handleDependentMemberSelect}
                  />
                )}
              </div>
            )}
            {caseType === 'Access Issue' && hasMemberSubmitRole && (
              <FormControl>
                <Typography>Patient Type(with provider)?*</Typography>
                <RadioGroup
                  row
                  onChange={e => {
                    setPatientType(e.target.value);
                    handlePatientType('newPatient', e.target.value);
                  }}
                  value={patientType}
                >
                  <FormControlLabel
                    control={<Radio color="primary" />}
                    value="new"
                    label="New Patient"
                  />
                  <FormControlLabel
                    control={<Radio color="primary" />}
                    value="existing"
                    label="Existing Patient"
                  />
                </RadioGroup>
              </FormControl>
            )}
            {hasMemberSubmitRole && (
              <TextInput
                label="Member ID"
                value={newCaseInfo?.memberId || newCaseInfo?.patientid || ''}
                inputProps={{
                  readOnly: hasMemberSubmitRole,
                }}
                onChange={val => handleOnChange(val, 'memberId')}
              />
            )}
            {hasClientSubmitRole && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <TextInput
                  label="Patient ID"
                  value={patientID}
                  onChange={val => handlePatientIDChange(val.trim().toUpperCase(), 'memberId')}
                />
                <Typography
                  variant="body2"
                  style={{ color: 'gray', marginTop: '2px', marginLeft: '2px' }}
                >
                  Enter Patient ID, Patient Name, and Date of Birth then click Search
                </Typography>
              </div>
            )}

            {hasMemberSubmitRole && (
              <TextInput
                label="Member Name"
                value={`${newCaseInfo?.firstName || ''} ${newCaseInfo?.lastName || ''}`}
                inputProps={{
                  readOnly: true,
                }}
                onChange={val => handleOnChange(val, 'firstName')}
              />
            )}
            {hasClientSubmitRole && (
              <TextInput
                label="Patient Name"
                value={patientName}
                onChange={val => handlePatientNameChange(val)}
              />
            )}
            {hasMemberSubmitRole && (
              <TextInput
                type="text"
                name="dob"
                label="Date of Birth"
                value={newCaseInfo?.dateOfBirth ? formatDate(newCaseInfo?.dateOfBirth) : ''}
                inputProps={{
                  readOnly: true,
                }}
                onChange={val => handleOnChange(val, 'dateOfBirth')}
              />
            )}
            {hasClientSubmitRole && (
              <div style={{ display: 'flex', gap: '1rem' }}>
                <TextInput
                  type="date"
                  name="dob"
                  label="Date of Birth"
                  placeholder="mm/dd/yyyy"
                  onChangeEvent={handleDateChange('dateOfBirth')}
                  value={patientDOB}
                />
                <Button
                  className={classes.searchBtn}
                  variant="contained"
                  onClick={handleSearchClick}
                  disabled={disableSearch}
                >
                  Search
                </Button>
              </div>
            )}
            {hasMemberSubmitRole && (
              <>
                <SelectInput
                  label="Preferred Method of Contact*"
                  options={['Phone', 'Email']}
                  value={newCaseInfo.preferredMethodOfContact}
                  onSelectChange={val => handleOnChange(val, 'preferredMethodOfContact')}
                />

                <TextInput
                  label={newCaseInfo?.preferredMethodOfContact === 'Phone' ? 'Phone*' : 'Phone'}
                  value={newCaseInfo.phone}
                  onChange={val => handleOnChange(val, 'phone')}
                  validators={['validatePhoneNumberLength']}
                  errorMessages={['Must be a valid phone number']}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    onInput: e => handleNumericInput(e, 10),
                  }}
                />
                <TextInput
                  label={newCaseInfo?.preferredMethodOfContact === 'Email' ? 'Email*' : 'Email'}
                  value={newCaseInfo?.email}
                  onChange={val => handleOnChange(val, 'email')}
                  validators={['isEmail']}
                  errorMessages={['Must be a valid email']}
                />
              </>
            )}
            {hasClientSubmitRole && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <TextInput
                    label="Phone*"
                    value={newCaseInfo.phone}
                    onChange={val => handleOnChange(val, 'phone')}
                    validators={['validatePhoneNumberLength']}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      onInput: e => handleNumericInput(e, 10),
                    }}
                  />
                  {errors && <Typography className={classes.errorLabel}>{errors.phone}</Typography>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <TextInput
                    label="Email*"
                    value={newCaseInfo?.email}
                    onChange={val => handleOnChange(val, 'email')}
                    validators={['isEmail']}
                  />
                  {errors && <Typography className={classes.errorLabel}>{errors.email}</Typography>}
                </div>
              </>
            )}
            <TextInput
              label="Address"
              value={newCaseInfo?.address || ''}
              onChange={val => handleOnChange(val, 'address')}
            />
            <Grid container spacing={2}>
              <Grid item xs={6} md={6}>
                <TextInput
                  label="State"
                  value={newCaseInfo?.state || ''}
                  onChange={val => handleOnChange(val, 'state')}
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextInput
                  label="City"
                  value={newCaseInfo?.city || ''}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  onChange={val => handleOnChange(val, 'city')}
                />
              </Grid>
            </Grid>
            <TextInput
              label="Zip"
              value={newCaseInfo?.zip || ''}
              onChange={val => handleOnChange(val, 'zip')}
              validators={['validateZipCode']}
              errorMessages={['Must be a valid Zip Code']}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 5,
                onInput: e => handleNumericInput(e, 5),
              }}
            />
            {hasClientSubmitRole && caseType === 'Access Issue' && (
              <FormControl>
                <Typography>Patient Type*</Typography>
                <RadioGroup
                  row
                  onChange={e => {
                    setClientPatientType(e.target.value);
                    handlePatientType('newPatient', e.target.value);
                  }}
                  value={clientPatientType}
                >
                  <FormControlLabel
                    control={<Radio color="primary" />}
                    value="new"
                    label="New Patient"
                  />
                  <FormControlLabel
                    control={<Radio color="primary" />}
                    value="existing"
                    label="Existing Patient"
                  />
                </RadioGroup>
              </FormControl>
            )}
            {hasMemberSubmitRole && (
              <>
                <TextInput
                  label="Health Plan Name"
                  value={newCaseInfo?.planname}
                  inputProps={{
                    readOnly: true,
                  }}
                  // options={[memberInfo?.planName]}
                  onChange={val => handleOnChange(val, 'planname')}
                />

                <TextInput
                  label="Group#"
                  value={newCaseInfo?.groupid}
                  inputProps={{
                    readOnly: newCaseInfo?.groupid,
                  }}
                  onChange={val => handleOnChange(val, 'groupid')}
                />
              </>
            )}
          </div>
        </div>
        <div className={classes.buttonStyle}>
          <Button variant="outlined" onClick={handleBack} className={classes.backBtn}>
            BACK
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={disableNextAndSubmit}
            className={classes.successBtn}
          >
            NEXT
          </Button>
        </div>
      </div>
    </AppLayout>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MemberDetail);
